/**
 * Shared Gong deep-link builder (B1, `plans/signals-tab-pr1/qa-batch2-plan.md`
 * §1.4/D-F) — the single home for the "Variant B" Gong Engage URL and its
 * open-redirect query string, previously hand-copied in two places:
 * `mini-apps/action-view/utils.ts:6-40` (the original) and
 * `src/services/terra-signals/gong-links.ts` (a byte-identical hand-kept
 * copy, per its own now-obsolete comment — core could not import mini-apps/**
 * until `mini-apps/_shared/` existed as the sanctioned shared layer both
 * sides may import). Both call sites now re-export from here so there is
 * exactly one implementation to keep in sync.
 *
 * Gong workspace + host. GONG_BASE is the hardcoded whitelisted redirect
 * target (the backend `/open/:id` endpoints rebuild the Gong URL from these
 * constants — never from a caller-supplied absolute URL — so there is no
 * open-redirect).
 */
export const GONG_WORKSPACE_ID = '2151574214928240805';
export const GONG_BASE = 'https://us-84989.app.gong.io';

/**
 * "Variant B" Gong Engage deep-link — lands the rep closer to the actual
 * to-do: `workspace-id` as a top-level query param, plus `activeTab: 'toDos'`
 * inside the `contact-params` JSON. Kept pure + hardcoded-base so the backend
 * redirect endpoint can rebuild the identical URL server-side (no open-redirect).
 */
export function gongEngageUrl(
  contactId: string | null | undefined,
  accountCrmId: string | null | undefined,
): string {
  if (!contactId) return `${GONG_BASE}/todos`;
  const params: Record<string, string> = {
    type: 'ENGAGE',
    contactCrmId: contactId,
  };
  if (accountCrmId) params.accountCrmId = accountCrmId;
  params.activeTab = 'toDos';
  const contactParams = encodeURIComponent(JSON.stringify(params));
  return `${GONG_BASE}/todos?workspace-id=${GONG_WORKSPACE_ID}&tab=ALL&quickFilter=%7B%7D&contact-params=${contactParams}`;
}

/**
 * The `?contactId=&accountId=` query-string fragment both clients append to
 * their own same-origin `/open/:id` redirect path — extracted verbatim from
 * `mini-apps/action-view/utils.ts`'s `gongOpenRedirectUrl` (ids
 * `encodeURIComponent`-ed, key omitted entirely when its id is absent, `''`
 * when both are absent). The redirect target itself is always rebuilt
 * server-side from `gongEngageUrl` — this fragment only carries the ids
 * through the same-origin hop, never a host/base.
 */
export function gongOpenQuery(
  contactId: string | null | undefined,
  accountId: string | null | undefined,
  /** The row's step type (e.g. `Call`) — analytics only, never affects the redirect target. */
  stepType?: string | null,
  /** The row's step number — analytics only (per-step SLA), never affects the redirect target. */
  stepNumber?: number | null,
): string {
  const parts: string[] = [];
  if (contactId) parts.push(`contactId=${encodeURIComponent(contactId)}`);
  if (accountId) parts.push(`accountId=${encodeURIComponent(accountId)}`);
  if (stepType) parts.push(`stepType=${encodeURIComponent(stepType)}`);
  if (typeof stepNumber === 'number') parts.push(`stepNumber=${stepNumber}`);
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/**
 * `/api/flow/signals/:id/open?contactId=…&accountId=…&stepType=…` — the
 * Signals-owned same-origin redirect route a Signals-tab verb button links to,
 * with its query built by `gongOpenQuery` so the ids-through-the-query-string
 * shape cannot drift between the two clients.
 */
export function gongOpenHref(
  signalId: string,
  contactId: string | null | undefined,
  accountId: string | null | undefined,
  stepType?: string | null,
  stepNumber?: number | null,
): string {
  return `/api/flow/signals/${encodeURIComponent(signalId)}/open${gongOpenQuery(contactId, accountId, stepType, stepNumber)}`;
}
